import { ChangeEvent, FC, Fragment, useState } from 'react';

import { getAllSourcesChecked, sourcesConfigFromDto } from 'utils/campaign';
import { addOrDelete } from 'utils/clickme';
import { CampaignSource, CampaignSubSource } from 'utils/constants/campaign';

import { VSpacing } from '@hh.ru/magritte-ui';

import { SourceCheckbox } from 'components/CpcFlexibleCreate/CpcForm/AdSources/SourceCheckbox/SourceCheckbox';
import { useSourceItemState } from 'components/CpcFlexibleCreate/CpcForm/AdSources/SourceItem/useSourceItemState';

import { setCampaignAllSources, setCampaignSubSource, Source } from 'store/models/createCampaign';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CpcFlexibleCreate/CpcForm/AdSources/SourceItem/SourceItem.module.less';

const DATA_QA = {
    [CampaignSource.HH]: 'hh-checkbox-label',
    // exp_PORTFOLIO-53635_start
    [CampaignSubSource.HHTopSearch]: 'hh-top-search-checkbox-label',
    [CampaignSubSource.HHBanners]: 'hh-banners-checkbox-label',
    // exp_PORTFOLIO-53635_end
    [CampaignSubSource.Direct]: 'direct-checkbox-label',
    [CampaignSource.Yandex]: 'yandex-checkbox-label',
    [CampaignSubSource.YandexSearch]: 'direct-checkbox-label',
    [CampaignSubSource.YandexNetwork]: 'yandexnetwork-checkbox-label',
    [CampaignSubSource.VK]: 'vk-checkbox-label',
    [CampaignSubSource.Social]: 'social-checkbox-label',
    [CampaignSubSource.Adwords]: 'adwords-checkbox-label',
    [CampaignSubSource.Facebook]: 'facebook-checkbox-label',
    [CampaignSubSource.Instagram]: 'instagram-checkbox-label',
    [CampaignSource.VKAds]: 'vk-ads-checkbox-label',
    [CampaignSubSource.VKAdsVK]: 'vk-ads-vk-checkbox-label',
    [CampaignSubSource.VKAdsOK]: 'vk-ads-ok-checkbox-label',
    [CampaignSubSource.VKAdsNetwork]: 'vk-ads-network-checkbox-label',
    [CampaignSubSource.VKAdsProject]: 'vk-ads-project-label',
};

interface Props {
    source: Source;
    isEditAdvertising: boolean;
    sendAdSourcesTouchEvent: () => void;
}

export const SourceItem: FC<Props> = ({ source, isEditAdvertising, sendAdSourcesTouchEvent }) => {
    const { adSource, savedSources, adSourcesConfig } = useSelector((state) => state.createCampaign);
    const dispatch = useDispatch();

    const { apiSource, subSources } = source;

    const { isSourceDisabled, isSourceChecked, isSourceIndeterminate, getSubSourceState } = useSourceItemState({
        source,
        adSource,
        savedSources,
        isEditAdvertising,
    });

    const [expanded, setExpanded] = useState<boolean>(true);
    const hasChildren = subSources.length > 1;

    const onChangeSource = (event: ChangeEvent<HTMLInputElement>, isSubSource: boolean) => {
        const { value, checked } = event.target;

        sendAdSourcesTouchEvent();

        const changedSubSources = isSubSource
            ? [value as CampaignSubSource]
            : subSources.filter(({ enabled }) => enabled).map(({ subSource }) => subSource);

        const newSources = changedSubSources.reduce(
            (tempResult, value) =>
                addOrDelete(tempResult, value, isSubSource ? checked : !isSourceIndeterminate) as CampaignSubSource[],
            adSource
        );

        const enabledSources = sourcesConfigFromDto(adSourcesConfig).enabledSources;
        const allSourcesChecked = getAllSourcesChecked(newSources, enabledSources);

        dispatch([setCampaignSubSource(newSources), setCampaignAllSources(allSourcesChecked)]);
    };

    return (
        <>
            <SourceCheckbox
                source={apiSource}
                checked={isSourceChecked}
                indeterminate={isSourceIndeterminate}
                disabled={isSourceDisabled}
                onChange={(event) => {
                    onChangeSource(event, false);
                }}
                dataQa={DATA_QA[apiSource]}
                hasChildren={hasChildren}
                expanded={expanded}
                handleExpandableClick={() => setExpanded((prevState) => !prevState)}
            />
            <VSpacing default={12} />
            {hasChildren && expanded && (
                <div className={styles.subSourcesContainer}>
                    {subSources.map(({ subSource, enabled }) => {
                        const { checked, disabled } = getSubSourceState(subSource, enabled);
                        return (
                            <Fragment key={subSource}>
                                <SourceCheckbox
                                    source={subSource}
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={(event) => {
                                        onChangeSource(event, true);
                                    }}
                                    dataQa={DATA_QA[subSource]}
                                    hasChildren={false}
                                />
                                <VSpacing default={12} />
                            </Fragment>
                        );
                    })}
                </div>
            )}
        </>
    );
};
